// src/components/PaginatedInvoiceTable.jsx

import React from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Receipt, ExternalLink, Download, Eye, CreditCard } from 'lucide-react';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { StatusBadge, Th, theadClass, IconButton, EmptyState, Spinner } from './portal/PortalUI';

const STATUS_LABELS = {
  draft: 'Draft',
  pending: 'Pending',
  opened: 'Pending',
  paid: 'Paid',
  overdue: 'Overdue',
  canceled: 'Canceled',
  refunded: 'Refunded',
};

const PaginatedInvoiceTable = ({
  invoices = [],
  isLoading,
  isPublishing = false,
  isUnpublishing = false,
  publishingInvoiceId = null,
  unpublishingInvoiceId = null,
  pagination,
  onPageChange,
  onViewDetails,
  onUnpublish,
  onSendInvoice,
  onOpenExternalLink, // New prop for opening external link (client portal)
  onDownloadInvoice,  // New prop for downloading invoice (client portal)
  onPayInvoice,       // New prop for paying invoice (client portal)
  getInvoiceStatusColor,
  context = 'admin' // New prop to determine the context ('admin' or 'client')
}) => {
  const statusLabel = (invoice) => {
    if (context === 'admin' && invoice.status === 'opened') return 'Opened';
    return STATUS_LABELS[invoice.status] || 'Unknown';
  };

  const renderPaginationControls = () => (
    <div className="flex items-center justify-between space-x-4 px-4 sm:px-6 py-4 border-t border-grayteck-200 dark:border-darkbg-600">
      <button
        onClick={() => onPageChange(pagination.currentPage - 1)}
        disabled={pagination.currentPage === 0}
        className="flex items-center gap-1.5 px-3.5 py-2 text-sm font-semibold rounded-md border border-grayteck-300 dark:border-darkbg-500 text-grayteck-700 dark:text-grayteck-200 bg-white dark:bg-darkbg-700 hover:bg-grayteck-100 dark:hover:bg-darkbg-600 transition-colors disabled:opacity-40 disabled:pointer-events-none"
      >
        <ChevronLeft className="w-4 h-4" /> Previous
      </button>

      <span className="text-sm text-grayteck-700 dark:text-grayteck-400">
        Page {pagination.currentPage + 1} of {pagination.totalPages} ({pagination.totalItems} total invoices)
      </span>

      <button
        onClick={() => onPageChange(pagination.currentPage + 1)}
        disabled={!pagination.hasMore}
        className="flex items-center gap-1.5 px-3.5 py-2 text-sm font-semibold rounded-md border border-grayteck-300 dark:border-darkbg-500 text-grayteck-700 dark:text-grayteck-200 bg-white dark:bg-darkbg-700 hover:bg-grayteck-100 dark:hover:bg-darkbg-600 transition-colors disabled:opacity-40 disabled:pointer-events-none"
      >
        Next <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-10">
        <Spinner label="Loading invoices…" />
      </div>
    );
  }

  if (!invoices.length) {
    return (
      <EmptyState icon={Receipt} title="No invoices">
        {context === 'client'
          ? 'Invoices for your engagements will appear here.'
          : 'Get started by creating a new invoice.'}
      </EmptyState>
    );
  }

  return (
    <>
      <div className="bg-white dark:bg-darkbg-800 rounded-xl border border-grayteck-200 dark:border-darkbg-600 shadow-card dark:shadow-card-dark overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className={theadClass}>
              <Th>Invoice</Th>
              <Th>Issued</Th>
              <Th>Due</Th>
              <Th>Amount</Th>
              <Th>Status</Th>
              <Th>Service</Th>
              <Th align="right">Actions</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-grayteck-200 dark:divide-darkbg-600">
            {invoices.map((invoice) => (
              <motion.tr
                key={invoice._id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="hover:bg-grayteck-50 dark:hover:bg-darkbg-700 transition-colors duration-150"
              >
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                    <span
                      className="text-sm font-semibold text-grayteck-900 dark:text-grayteck-100"
                      data-tooltip-id={`invoice-${invoice._id}`}
                      data-tooltip-content={new Date(invoice.created_at).toLocaleString()}
                    >
                      {invoice.invoice_number}
                    </span>
                    <Tooltip
                      id={`invoice-${invoice._id}`}
                      className="bg-grayteck-900 text-white p-2 rounded shadow-md"
                    />
                </td>
                <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-grayteck-700 dark:text-grayteck-400">
                  {invoice.issued_at ? new Date(invoice.issued_at).toLocaleDateString() : 'N/A'}
                </td>
                <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-grayteck-700 dark:text-grayteck-400">
                    {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : <span className="italic">Due on receipt</span>}
                </td>
                <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm font-semibold text-grayteck-900 dark:text-grayteck-100 tabular-nums">
                  ${invoice.total.toFixed(2)}
                </td>
                <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                  <StatusBadge status={invoice.status} label={statusLabel(invoice)} />
                </td>
                <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-grayteck-700 dark:text-grayteck-400">
                  {invoice.service_code || 'N/A'}
                </td>
                <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end items-center gap-1">
                      {/* Admin Dashboard Actions */}
                      {context === 'admin' && (
                        <>
                          {/* Unpublish Button */}
                            {(invoice.status === 'pending' || invoice.status === 'opened') && (
                            <button
                                onClick={() => onUnpublish(invoice)}
                                disabled={isUnpublishing && unpublishingInvoiceId === invoice._id}
                                className="text-red-600 hover:text-red-700 dark:text-red-400
                                        dark:hover:text-red-300 inline-flex items-center space-x-1 text-sm font-semibold px-2 py-1 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                            >
                                {isUnpublishing && unpublishingInvoiceId === invoice._id ? (
                                  <span className="flex items-center">
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-red-600 dark:text-red-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Unpublishing...
                                  </span>
                                ) : (
                                  <span>Unpublish</span>
                                )}
                            </button>
                            )}
                          {/* Send Button */}
                          {invoice.status === 'draft' && (
                      <button
                        onClick={() => onSendInvoice(invoice)}
                        disabled={isPublishing && publishingInvoiceId === invoice._id}
                        className="text-grayteck-700 hover:text-grayteck-900 dark:text-grayteck-300
                                 dark:hover:text-grayteck-100 inline-flex items-center space-x-1 text-sm font-semibold px-2 py-1 rounded-md hover:bg-grayteck-100 dark:hover:bg-darkbg-600 transition-colors"
                      >
                        {isPublishing && publishingInvoiceId === invoice._id ? (
                          <span className="flex items-center">
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Processing...
                          </span>
                        ) : (
                          <span>Send</span>
                        )}
                      </button>
                    )}
                        </>
                      )}

                      {/* Client Portal Actions */}
                      {context === 'client' && (
                        <>
                          {/* Pay Now Button */}
                          {onPayInvoice && ['pending', 'opened', 'overdue'].includes(invoice.status) && (
                            <button
                              onClick={() => onPayInvoice(invoice)}
                              className="inline-flex items-center gap-1.5 mr-1 px-2.5 py-1.5 rounded-md text-xs font-bold
                                       text-accentfg bg-gold-400 hover:bg-gold-300 active:bg-gold-500 shadow-sm transition-colors"
                              data-tooltip-id={`pay-tooltip-${invoice._id}`}
                              data-tooltip-content="Pay Invoice"
                            >
                              <CreditCard className="w-4 h-4" />
                              Pay
                              <Tooltip
                                id={`pay-tooltip-${invoice._id}`}
                                place="top"
                                effect="solid"
                              />
                            </button>
                          )}
                          {/* Open External Link Button */}
                          {invoice.shareable_link && (
                            <IconButton
                              title="Open External Link"
                              onClick={() => onOpenExternalLink(invoice.document_id)}
                              data-tooltip-id={`open-link-tooltip-${invoice._id}`}
                              data-tooltip-content="Open External Link"
                            >
                              <ExternalLink className="w-[18px] h-[18px]" />
                              <Tooltip
                                id={`open-link-tooltip-${invoice._id}`}
                                place="top"
                                effect="solid"
                              />
                            </IconButton>
                          )}
                          {/* Download Invoice Button */}
                          <IconButton
                            title="Download Invoice"
                            onClick={() => onDownloadInvoice(invoice.document_id)}
                            data-tooltip-id={`download-tooltip-${invoice._id}`}
                            data-tooltip-content="Download Invoice"
                          >
                            <Download className="w-[18px] h-[18px]" />
                            <Tooltip
                              id={`download-tooltip-${invoice._id}`}
                              place="top"
                              effect="solid"
                            />
                          </IconButton>
                        </>
                      )}

                      {/* Common Action: View Details. For the client portal this
                          opens the in-portal invoice detail page; the separate
                          "Open External Link" button above is the actual
                          external-link action. */}
                    <IconButton
                        title="View Details"
                        onClick={() => onViewDetails(invoice)}
                        data-tooltip-id={`view-details-tooltip-${invoice._id}`}
                        data-tooltip-content="View Details"
                    >
                        <Eye className="w-[18px] h-[18px]" />
                        <Tooltip
                          id={`view-details-tooltip-${invoice._id}`}
                          place="top"
                          effect="solid"
                        />
                    </IconButton>
                  </div>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
      </div>

      {/* Render controls off the server pagination metadata (total pages),
          not the current page's item count -- the API already returns
          server-paginated pages capped at the page size, so a raw item-count
          threshold never fires. */}
      {pagination?.totalPages > 1 && renderPaginationControls()}
    </>
  );
};

export default PaginatedInvoiceTable;
